/**
 * Solufy Accordion System
 * Handles accessible, smooth collapsible panels matching the side-by-side dense content layout.
 */
export function mountAccordions(root = document) {
	const accordions = root.querySelectorAll("[data-solufy-accordion]");

	accordions.forEach((container) => {
		const allowMultiple = container.hasAttribute("data-allow-multiple");
		const items = container.querySelectorAll(".solufy-accordion-item");

		items.forEach((item) => {
			const header = item.querySelector(".solufy-accordion-header");
			const body = item.querySelector(".solufy-accordion-body");
			const icon = item.querySelector(".solufy-accordion-icon");

			if (!header || !body) return;

			// Initialize initial state based on class
			const isInitiallyOpen = item.classList.contains("is-open");
			header.setAttribute("aria-expanded", isInitiallyOpen ? "true" : "false");
			if (icon) {
				icon.textContent = isInitiallyOpen ? "−" : "+";
			}
			if (isInitiallyOpen) {
				body.style.maxHeight = body.scrollHeight + "px";
			} else {
				body.style.maxHeight = "0px";
			}

			header.addEventListener("click", () => {
				const isOpen = item.classList.contains("is-open");

				if (!allowMultiple && !isOpen) {
					// Close other items in the same container
					items.forEach((other) => {
						if (other !== item && other.classList.contains("is-open")) {
							other.classList.remove("is-open");
							const otherHeader = other.querySelector(".solufy-accordion-header");
							const otherBody = other.querySelector(".solufy-accordion-body");
							const otherIcon = other.querySelector(".solufy-accordion-icon");
							if (otherHeader) otherHeader.setAttribute("aria-expanded", "false");
							if (otherBody) otherBody.style.maxHeight = "0px";
							if (otherIcon) otherIcon.textContent = "+";
						}
					});
				}

				if (isOpen) {
					item.classList.remove("is-open");
					header.setAttribute("aria-expanded", "false");
					body.style.maxHeight = "0px";
					if (icon) icon.textContent = "+";
				} else {
					item.classList.add("is-open");
					header.setAttribute("aria-expanded", "true");
					body.style.maxHeight = body.scrollHeight + "px";
					if (icon) icon.textContent = "−";
				}
			});

			// Keyboard support
			header.addEventListener("keydown", (e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					header.click();
				}
			});
		});
	});

	mountFaqDetails(root);
}

function mountFaqDetails(root) {
	const groupSelectors = [
		".studio-faq",
		".solufy-faq-list",
		".industry-faq",
		".service-faq",
		".service-faq-accordion",
		".erpnext-faq",
		".faq-container",
		".faq-answer",
		"[data-article-content]",
		".solufy-article-content",
		".solufy-article-body",
		".solufy-richtext"
	];

	const groups = new Set();
	groupSelectors.forEach((sel) => {
		root.querySelectorAll(sel).forEach((el) => {
			if (el.querySelector("details")) {
				groups.add(el);
			}
		});
	});

	// Also catch any standalone details in main, article, or site container
	const allDetails = root.querySelectorAll("main details, article details, .solufy-site details");
	allDetails.forEach((d) => {
		const parentGroup = d.closest(groupSelectors.join(","));
		if (parentGroup) {
			groups.add(parentGroup);
		} else if (d.parentElement) {
			groups.add(d.parentElement);
		}
	});

	groups.forEach((group) => {
		if (group.dataset.faqAccordionMounted === "true") return;
		group.dataset.faqAccordionMounted = "true";
		group.classList.add("solufy-faq-motion");

		const items = group.querySelectorAll("details");
		let foundOpenItem = false;
		items.forEach((item) => {
			// Strip legacy inline styles that override modern CSS
			item.removeAttribute("style");
			const summary = item.querySelector(":scope > summary");
			if (summary) {
				summary.removeAttribute("style");
				// Strip legacy inline icon elements so summary::after is the only indicator
				summary.querySelectorAll(".solufy-faq-entry__icon, .industry-faq-icon, .faq-icon-indicator").forEach((iconEl) => iconEl.remove());
			}
			const answers = item.querySelectorAll(":scope > div, :scope > p, :scope > .industry-faq-content, :scope > .solufy-faq-entry__answer");
			answers.forEach((ans) => ans.removeAttribute("style"));

			if (item.open && foundOpenItem) item.open = false;
			if (item.open) foundOpenItem = true;

			if (!summary) return;
			if (item.dataset.faqListenerMounted === "true") return;
			item.dataset.faqListenerMounted = "true";

			summary.addEventListener("click", (event) => {
				event.preventDefault();
				const shouldOpen = !(faqAnimations.get(item)?.shouldOpen ?? item.open);
				if (shouldOpen) {
					items.forEach((other) => {
						if (other !== item && other.open) animateFaqDetails(other, false);
					});
				}
				animateFaqDetails(item, shouldOpen);
			});
		});
	});
}

const faqAnimations = new WeakMap();

function animateFaqDetails(item, shouldOpen) {
	const startHeight = `${item.offsetHeight}px`;
	faqAnimations.get(item)?.animation.cancel();
	faqAnimations.delete(item);

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	const summary = item.querySelector(":scope > summary");
	if (!summary || reduceMotion) {
		item.open = shouldOpen;
		item.style.removeProperty("overflow");
		return;
	}

	let endHeight;
	if (shouldOpen) {
		item.open = true;
		endHeight = `${item.scrollHeight}px`;
	} else {
		item.open = false;
		endHeight = `${item.offsetHeight}px`;
		item.open = true;
	}

	item.style.overflow = "hidden";
	const animation = item.animate(
		{ height: [startHeight, endHeight] },
		{ duration: 280, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
	);
	faqAnimations.set(item, { animation, shouldOpen });

	animation.onfinish = () => {
		item.open = shouldOpen;
		item.style.removeProperty("height");
		item.style.removeProperty("overflow");
		faqAnimations.delete(item);
		animation.cancel();
	};
}
